import { z } from 'zod';

/**
 * A game's look for MOGZO's own panels over it (M19): the achievement pop-up, the leaderboard,
 * the achievements list, the pause menu and the sign-in prompt. The developer designs it in the
 * dashboard's Game UI editor; the layout and wording stay MOGZO's. Colours and the font are the
 * same everywhere; every other value can differ by view (Computer, Tablet or Phone, each for
 * the splash and the game), and a view takes what it doesn't set from the views above it.
 */

import {
  resolveValue,
  UI_BADGES,
  UI_BASE,
  UI_CONTRAST_MIN,
  UI_CORNER_MAX,
  UI_EDGES,
  UI_FONTS,
  UI_LIMITS,
  UI_PRESET_IDS,
  UI_SCROLLBARS,
  UI_SWITCH_SHAPES,
  UI_TARGET_KEYS,
  UI_TARGETS,
  UI_TEXT_PX,
  UI_TEXT_SIZES,
  UI_VIEWS,
  type UiCorners,
  type UiNumberKey,
  type UiPresetId,
  type UiTarget,
  type UiValues,
  type UiView,
  type UiViews,
  uiContrastChecks,
} from './game-ui-tokens';

const colour = z.string().regex(/^#[0-9a-f]{6}$/i, 'Use a colour like #1b152f');

export const uiColoursSchema = z.object({
  panel: colour,
  text: colour,
  muted: colour,
  accent: colour,
  onAccent: colour,
  field: colour,
  line: colour,
});

const limited = (key: UiNumberKey) =>
  z.int().min(UI_LIMITS[key].hardMin).max(UI_LIMITS[key].hardMax);
const corner = z.int().min(0).max(UI_CORNER_MAX);

/** Every value a view can set, each with its limits. */
const uiValuesSchema = z.object({
  dim: limited('dim'),
  textSize: limited('textSize'),
  corners: z.object({ tl: corner, tr: corner, br: corner, bl: corner }),
  btnRadius: limited('btnRadius'),
  btnHeight: limited('btnHeight'),
  pad: limited('pad'),
  gap: limited('gap'),
  scrollbar: z.enum(UI_SCROLLBARS),
  switches: z.enum(UI_SWITCH_SHAPES),
  progress: limited('progress'),
  edge: z.enum(UI_EDGES),
  offset: limited('offset'),
  width: limited('width'),
  height: limited('height'),
  seconds: limited('seconds'),
  rowHeight: limited('rowHeight'),
  badges: z.enum(UI_BADGES),
  iconSize: limited('iconSize'),
}) satisfies z.ZodType<UiValues>;

/** What Theme or a panel sets in one view: only its own values, each optional. */
function targetSchema(target: UiTarget) {
  const keys = Object.fromEntries(UI_TARGET_KEYS[target].map((key) => [key, true])) as {
    [K in (typeof UI_TARGET_KEYS)[typeof target][number]]: true;
  };
  return uiValuesSchema.pick(keys).partial().strict();
}

const viewSchema = z
  .object(Object.fromEntries(UI_TARGETS.map((t) => [t, targetSchema(t).optional()])))
  .strict();

export const gameUiSchema = z
  .object({
    version: z.literal(2),
    /** Which preset it started from; `custom` once a colour or the font was changed. */
    preset: z.enum([...UI_PRESET_IDS, 'custom']),
    colours: uiColoursSchema,
    font: z.enum(UI_FONTS),
    /** What each view sets; anything missing comes from the views above it, then MOGZO's. */
    views: z.partialRecord(z.enum(UI_VIEWS as [UiView, ...UiView[]]), viewSchema),
  })
  .superRefine((look, ctx) => {
    for (const check of uiContrastChecks(look.colours)) {
      if (!check.ok) {
        ctx.addIssue({
          code: 'custom',
          path: ['colours', check.key],
          message: `${check.label} is too faint (${check.ratio.toFixed(1)} : 1); it needs at least ${UI_CONTRAST_MIN} : 1.`,
        });
      }
    }
  });
export type GameUiLook = Omit<z.infer<typeof gameUiSchema>, 'views'> & { views: UiViews };

type PresetParts = Pick<GameUiLook, 'colours' | 'font'> & {
  name: string;
  /** Panel and button corners, set on Computer · Game's Theme. */
  radius: number;
  buttonRadius: number;
};

/** Looks to start from in the Game UI editor. */
export const UI_PRESETS: Record<UiPresetId, PresetParts> = {
  'mogzo-dark': {
    name: 'MOGZO dark',
    colours: {
      panel: '#1b152f',
      text: '#ffffff',
      muted: '#b9b3cf',
      accent: '#ffcb2f',
      onAccent: '#1b152f',
      field: '#2a2445',
      line: '#3a3352',
    },
    radius: 18,
    buttonRadius: 25,
    font: 'dm',
  },
  'mogzo-light': {
    name: 'MOGZO light',
    colours: {
      panel: '#ffffff',
      text: '#1b152f',
      muted: '#6b6680',
      accent: '#ffcb2f',
      onAccent: '#1b152f',
      field: '#f3f1f8',
      line: '#e4e2ec',
    },
    radius: 18,
    buttonRadius: 25,
    font: 'dm',
  },
  neon: {
    name: 'Neon',
    colours: {
      panel: '#120e2b',
      text: '#f3f0ff',
      muted: '#a9a2cc',
      accent: '#7cf3ff',
      onAccent: '#0b0a1d',
      field: '#1d1842',
      line: '#2c2650',
    },
    radius: 6,
    buttonRadius: 6,
    font: 'tech',
  },
  candy: {
    name: 'Candy',
    colours: {
      panel: '#fff4fa',
      text: '#3a1030',
      muted: '#7a4a6c',
      accent: '#c42a6b',
      onAccent: '#ffffff',
      field: '#ffe6f2',
      line: '#f5d6e8',
    },
    radius: 26,
    buttonRadius: 25,
    font: 'rounded',
  },
};

const allCorners = (n: number): UiCorners => ({ tl: n, tr: n, br: n, bl: n });

/**
 * A preset over a look: its colours and font, and its corners on Computer · Game's Theme (the
 * other views keep what they set).
 */
export function applyPreset(look: GameUiLook, id: UiPresetId): GameUiLook {
  const { name: _, radius, buttonRadius, ...parts } = UI_PRESETS[id];
  const base = look.views['computer.game'] ?? {};
  const theme: Partial<UiValues> = {
    ...base.theme,
    corners: allCorners(radius),
    btnRadius: buttonRadius,
  };
  if (radius === UI_BASE.theme.corners.tl) delete theme.corners;
  if (buttonRadius === UI_BASE.theme.btnRadius) delete theme.btnRadius;
  const views: UiViews = { ...look.views, 'computer.game': { ...base, theme } };
  if (!Object.keys(theme).length) {
    const { theme: __, ...rest } = base;
    views['computer.game'] = rest;
    if (!Object.keys(rest).length) delete views['computer.game'];
  }
  return { ...look, preset: id, colours: { ...parts.colours }, font: parts.font, views };
}

/** A look from a preset, with MOGZO's sizes and spacing everywhere. */
export function lookFromPreset(id: UiPresetId): GameUiLook {
  return applyPreset(
    { version: 2, preset: id, colours: UI_PRESETS[id].colours, font: 'dm', views: {} },
    id,
  );
}

/** What every game has until its developer saves a look. */
export const DEFAULT_GAME_UI: GameUiLook = lookFromPreset('mogzo-dark');

/** Looks saved before the editor had views (CLI 0.8.0 still draws these). */
export const legacyGameUiSchema = z.object({
  preset: z.enum([...UI_PRESET_IDS, 'custom']),
  colours: uiColoursSchema,
  dim: z.int().min(0).max(80),
  radius: z.int().min(0).max(32),
  /** 26 means fully round. */
  buttonRadius: z.int().min(0).max(26),
  font: z.enum(UI_FONTS),
  textSize: z.enum(UI_TEXT_SIZES),
  buttonHeight: z.int().min(44).max(64),
  padding: z.int().min(12).max(28),
  gap: z.int().min(4).max(18),
  switches: z.enum(UI_SWITCH_SHAPES),
  scrollbars: z.enum(UI_SCROLLBARS),
  progress: z.int().min(4).max(12),
  popup: z.object({
    edge: z.enum(UI_EDGES),
    offset: z.int().min(0).max(120),
    seconds: z.int().min(3).max(6),
  }),
});
export type LegacyGameUiLook = z.infer<typeof legacyGameUiSchema>;

/** Only the values that differ from MOGZO's. */
function changed<T extends object>(values: T, base: Partial<T>): Partial<T> {
  return Object.fromEntries(
    Object.entries(values).filter(
      ([key, value]) => JSON.stringify(value) !== JSON.stringify(base[key as keyof T]),
    ),
  ) as Partial<T>;
}

/** A look saved before views, as Computer · Game (every other view takes from it). */
export function fromLegacyLook(old: LegacyGameUiLook): GameUiLook {
  const theme = changed<Partial<UiValues>>(
    {
      dim: old.dim,
      textSize: UI_TEXT_PX[old.textSize],
      corners: allCorners(old.radius),
      // Fully round: at least half the button's height.
      btnRadius:
        old.buttonRadius >= 26
          ? Math.max(UI_BASE.theme.btnRadius, Math.ceil(old.buttonHeight / 2))
          : old.buttonRadius,
      btnHeight: old.buttonHeight,
      pad: old.padding,
      gap: old.gap,
      scrollbar: old.scrollbars,
      switches: old.switches,
      progress: old.progress,
    },
    UI_BASE.theme,
  );
  const popup = changed<Partial<UiValues>>(old.popup, UI_BASE.popup);
  const base = {
    ...(Object.keys(theme).length ? { theme } : {}),
    ...(Object.keys(popup).length ? { popup } : {}),
  };
  return {
    version: 2,
    preset: old.preset,
    colours: old.colours,
    font: old.font,
    views: Object.keys(base).length ? { 'computer.game': base } : {},
  };
}

/** A stored or received look, from before views or after; null when it's neither. */
export function readGameUiLook(raw: unknown): GameUiLook | null {
  const now = gameUiSchema.safeParse(raw);
  if (now.success) return now.data as GameUiLook;
  const old = legacyGameUiSchema.safeParse(raw);
  return old.success ? fromLegacyLook(old.data) : null;
}

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

/** Computer · Game's look in the old shape, for CLI 0.8.0's emulator. */
export function toLegacyLook(look: GameUiLook): LegacyGameUiLook {
  const value = <K extends keyof UiValues>(target: UiTarget, key: K) =>
    resolveValue(look.views, 'computer.game', target, key).value;
  const height = clamp(value('theme', 'btnHeight'), 44, 64);
  const btnRadius = value('theme', 'btnRadius');
  const text = value('theme', 'textSize');
  return {
    preset: look.preset,
    colours: look.colours,
    font: look.font,
    dim: clamp(value('theme', 'dim'), 0, 80),
    radius: clamp(value('theme', 'corners').tl, 0, 32),
    buttonRadius: btnRadius * 2 >= height ? 26 : clamp(btnRadius, 0, 26),
    textSize: text <= 14 ? 's' : text >= 17 ? 'l' : 'm',
    buttonHeight: height,
    padding: clamp(value('theme', 'pad'), 12, 28),
    gap: clamp(value('theme', 'gap'), 4, 18),
    switches: value('theme', 'switches'),
    scrollbars: value('theme', 'scrollbar'),
    progress: clamp(value('theme', 'progress'), 4, 12),
    popup: {
      edge: value('popup', 'edge'),
      offset: clamp(value('popup', 'offset'), 0, 120),
      seconds: clamp(value('popup', 'seconds'), 3, 6),
    },
  };
}
